// src/services/auth.js

const API_URL = (process.env.REACT_APP_API_URL || "http://localhost:4000").replace(/\/$/, "");

const SESSION_KEY = "zedro_session";

/* ========= Helpers de sesión ========= */

export function getCurrentSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setSession(session) {
  if (!session) {
    localStorage.removeItem(SESSION_KEY);
    return;
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getCurrentUser() {
  const s = getCurrentSession();
  return s?.user || null;
}

export async function restoreSession() {
  return getCurrentSession();
}

/* ========= NUEVO: ACTIVACIÓN ========= */

export async function activateAccount(token, password) {
  const res = await fetch(`${API_URL}/api/activate/${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ password }),
  });

  const text = await res.text().catch(() => "");
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || "No se pudo activar la cuenta";
    throw new Error(msg);
  }

  // 🔥 CLAVE: guardar sesión inmediatamente
  if (data?.token && data?.user) {
    setSession({
      token: data.token,
      user: data.user,
    });
  }

  return data;
}

/* ========= Fuente de verdad ========= */

export async function me() {
  const session = getCurrentSession();
  const token = session?.token;

  if (!token) {
    return { ok: false, user: null };
  }

  const res = await fetch(`${API_URL}/api/me`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    cache: "no-store",
  });

  if (!res.ok) {
    throw new Error("No se pudo rehidratar /api/me");
  }

  const data = await res.json();

  // 🔥 CLAVE: garantizar estructura consistente
  return {
    ok: true,
    user: data.user || null,
    subscriptions: data.subscriptions || [],
    products: data.products || [],
    core_progress: data.core_progress || []
  };
}

/* ========= LOGIN ========= */

export async function login(email, password) {
  const payload = { email, password };

  const res = await fetch(`${API_URL}/api/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.message || data?.error || `Login falló (${res.status})`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  setSession(data);
  return data;
}

/* ========= updateProfile ========= */

export async function updateProfile(payload) {
  const session = getCurrentSession();
  const token = session?.token;

  const res = await fetch(`${API_URL}/api/me/profile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text().catch(() => "");
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg = data?.error || "No se pudo actualizar el perfil";
    throw new Error(msg);
  }

  if (session?.token && data?.user) {
    setSession({ ...session, user: data.user });
  }

  return data;
}

/* ========= PASSWORD ========= */

export async function requestPasswordReset(email) {
  const res = await fetch(`${API_URL}/api/password/forgot`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ email }),
  });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg =
      data?.message ||
      data?.error ||
      `No se pudo solicitar el restablecimiento (${res.status})`;
    throw new Error(msg);
  }

  return data;
}

export async function resetPassword({ token, password }) {
  const res = await fetch(`${API_URL}/api/password/reset`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ token, password }),
  });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    const msg =
      data?.message ||
      data?.error ||
      `No se pudo restablecer la contraseña (${res.status})`;
    throw new Error(msg);
  }

  if (data?.user && data?.token) {
    setSession(data);
  }

  return data;
}

/* ========= LOGOUT ========= */

export async function logout() {
  setSession(null);

  try {
    localStorage.setItem("zedro::logout", Date.now().toString());
  } catch {}
}

export async function cancelMembership() {
  const session = getCurrentSession();
  const token = session?.token;

  if (!token) {
    throw new Error("No hay sesión activa.");
  }

  const res = await fetch(`${API_URL}/api/member/billing/cancel-membership`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.error || "No se pudo cancelar la suscripción.");
  }

  return data;
}

export async function reactivateMembership() {
  const session = getCurrentSession();
  const token = session?.token;

  if (!token) {
    throw new Error("No hay sesión activa.");
  }

  const res = await fetch(`${API_URL}/api/member/billing/reactivate-membership`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.error || "No se pudo reactivar la membresía.");
  }

  return data;
}
